import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { formatDate, cn } from '@/lib/utils'
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline'

interface AuditLog {
  _id: string
  adminId: {
    _id: string
    email: string
  }
  action: string
  resource: string
  resourceId?: string
  details?: Record<string, any>
  ipAddress: string
  userAgent: string
  createdAt: string
}

const actionColors: Record<string, string> = {
  create: 'text-green-400',
  update: 'text-blue-400',
  delete: 'text-red-400',
  login: 'text-purple-400',
  logout: 'text-gray-400',
  export: 'text-yellow-400',
}

const actionLabels: Record<string, string> = {
  create: 'Создание',
  update: 'Обновление',
  delete: 'Удаление',
  login: 'Вход',
  logout: 'Выход',
  export: 'Экспорт',
  block: 'Блокировка',
  unblock: 'Разблокировка',
  premium_toggle: 'Премиум',
  publish: 'Публикация',
  unpublish: 'Снятие',
}

const resourceLabels: Record<string, string> = {
  admin: 'Администратор',
  user: 'Пользователь',
  level: 'Уровень',
  lesson: 'Урок',
  news: 'Новость',
  chat: 'Чат',
  settings: 'Настройки',
}

export default function AuditPage() {
  const [search, setSearch] = useState('')
  const [actionFilter, setActionFilter] = useState('')
  const [resourceFilter, setResourceFilter] = useState('')
  const [page, setPage] = useState(1)
  
  const { data, isLoading } = useQuery({
    queryKey: ['audit', search, actionFilter, resourceFilter, page],
    queryFn: async () => {
      const params = new URLSearchParams()
      params.append('page', String(page))
      params.append('limit', '50')
      if (search) params.append('search', search)
      if (actionFilter) params.append('action', actionFilter)
      if (resourceFilter) params.append('resource', resourceFilter)
      
      const res = await api.get<{ data: { logs: AuditLog[]; total: number; totalPages: number } }>(
        `/admin/audit?${params}`
      )
      return res.data.data
    },
  })
  
  return (
    <div className="space-y-6 animate-fadeIn">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-white">Аудит</h1>
      </div>
      
      {/* Filters */}
      <div className="card">
        <div className="flex flex-wrap gap-4">
          <div className="relative flex-1 min-w-[200px]">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Поиск по админу или IP..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10"
            />
          </div>
          
          <select
            className="w-40"
            value={actionFilter}
            onChange={(e) => setActionFilter(e.target.value)}
          >
            <option value="">Все действия</option>
            <option value="create">Создание</option>
            <option value="update">Обновление</option>
            <option value="delete">Удаление</option>
            <option value="login">Вход</option>
            <option value="logout">Выход</option>
            <option value="export">Экспорт</option>
          </select>
          
          <select
            className="w-40"
            value={resourceFilter}
            onChange={(e) => setResourceFilter(e.target.value)}
          >
            <option value="">Все ресурсы</option>
            <option value="admin">Администраторы</option>
            <option value="user">Пользователи</option>
            <option value="level">Уровни</option>
            <option value="lesson">Уроки</option>
            <option value="news">Новости</option>
            <option value="chat">Чаты</option>
            <option value="settings">Настройки</option>
          </select>
        </div>
      </div>
      
      {/* Table */}
      <div className="card overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-700">
              <th className="text-left py-3 px-4 font-medium text-gray-400">Дата</th>
              <th className="text-left py-3 px-4 font-medium text-gray-400">Админ</th>
              <th className="text-left py-3 px-4 font-medium text-gray-400">Действие</th>
              <th className="text-left py-3 px-4 font-medium text-gray-400">Ресурс</th>
              <th className="text-left py-3 px-4 font-medium text-gray-400">Детали</th>
              <th className="text-left py-3 px-4 font-medium text-gray-400">IP</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={6} className="text-center py-8 text-gray-400">
                  Загрузка...
                </td>
              </tr>
            ) : data?.logs.length === 0 ? (
              <tr>
                <td colSpan={6} className="text-center py-8 text-gray-400">
                  Записей не найдено
                </td>
              </tr>
            ) : (
              data?.logs.map((log) => (
                <tr key={log._id} className="border-b border-gray-700/50 hover:bg-gray-700/20">
                  <td className="py-3 px-4 text-gray-400 whitespace-nowrap">
                    {formatDate(log.createdAt, 'dd.MM.yyyy HH:mm:ss')}
                  </td>
                  <td className="py-3 px-4 text-white">
                    {log.adminId?.email || 'Система'}
                  </td>
                  <td className="py-3 px-4">
                    <span className={cn('font-medium', actionColors[log.action] || 'text-gray-300')}>
                      {actionLabels[log.action] || log.action}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-gray-300">
                    <div>
                      {resourceLabels[log.resource] || log.resource}
                      {log.resourceId && (
                        <span className="text-xs text-gray-500 ml-2">
                          {log.resourceId.slice(-6)}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="py-3 px-4 text-gray-400 max-w-xs truncate">
                    {log.details ? (
                      <span title={JSON.stringify(log.details, null, 2)}>
                        {Object.entries(log.details)
                          .slice(0, 2)
                          .map(([k, v]) => `${k}: ${v}`)
                          .join(', ')}
                      </span>
                    ) : (
                      '-'
                    )}
                  </td>
                  <td className="py-3 px-4 text-gray-500 text-sm">
                    {log.ipAddress}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      
      {/* Pagination */}
      {data && data.totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-400">
            Всего: {data.total} записей
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="btn-secondary"
            >
              Назад
            </button>
            <span className="px-4 py-2 text-gray-400">
              {page} / {data.totalPages}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(data.totalPages, p + 1))}
              disabled={page === data.totalPages}
              className="btn-secondary"
            >
              Вперёд
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
